import React, { useState } from 'react';

interface IzakayaTimelineProps {
  lang: 'ja' | 'en';
}

const content = {
  ja: {
    badge: '体験の流れ',
    title: 'こんな夜を過ごします',
    subtitle: 'せんべろ・はしご酒・ローカル居酒屋…東京の本物の夜を一緒に楽しみましょう',
    steps: [
      {
        time: '18:00',
        icon: 'ri-map-pin-2-line',
        label: 'スタート',
        title: '集合・待ち合わせ',
        description: '荏原町・中延・戸越・武蔵小山のいずれかの駅で待ち合わせ。観光客が来ないローカルエリアからスタートします。',
        tag: 'ローカル駅集合',
        color: 'amber',
        image: 'https://public.readdy.ai/ai/img_res/52f18576c2e999c62d086ccbdb437201.jpg',
      },
      {
        time: '18:15',
        icon: 'ri-store-2-line',
        label: '1軒目',
        title: 'せんべろ居酒屋へ',
        description: '1,000円でベロベロになれる「せんべろ」の聖地へ。地元のおじさんたちに混じって、安くて旨い一杯を楽しみます。',
        tag: '¥1,000〜',
        color: 'orange',
        image: 'https://readdy.ai/api/search-image?query=Japanese%20senbero%20izakaya%20bar%20interior%20counter%20seats%20local%20old%20men%20drinking%20beer%20sake%20warm%20yellow%20light%20small%20cramped%20authentic%20Tokyo%20shitamachi%20cheap%20standing%20bar%20smoky%20atmosphere%20retro%20signboard&width=600&height=400&seq=timeline-step2&orientation=landscape',
      },
      {
        time: '19:15',
        icon: 'ri-walk-line',
        label: '移動',
        title: '商店街をぶらぶら',
        description: '昭和レトロな商店街を歩きながら次の店へ。地元の人しか知らない路地裏スポットも案内します。',
        tag: '徒歩5分',
        color: 'yellow',
        image: 'https://readdy.ai/api/search-image?query=Tokyo%20Showa%20retro%20shotengai%20shopping%20arcade%20night%20illuminated%20lanterns%20colorful%20banners%20narrow%20covered%20street%20local%20shops%20warm%20glow%20nostalgic%20Japan%20evening%20people%20strolling%20old%20fashioned%20signboards&width=600&height=400&seq=timeline-step3&orientation=landscape',
      },
      {
        time: '19:30',
        icon: 'ri-goblet-line',
        label: '2軒目',
        title: 'はしご酒・2軒目',
        description: '焼き鳥・もつ焼き・煮込みなど、東京下町の定番おつまみと一緒に。地元常連客との交流も楽しみのひとつ。',
        tag: 'もつ焼き・焼き鳥',
        color: 'red',
        image: 'https://readdy.ai/api/search-image?query=Japanese%20yakitori%20motsu%20yaki%20grilled%20skewers%20izakaya%20counter%20charcoal%20grill%20smoke%20rising%20beer%20glasses%20nikomi%20stew%20Tokyo%20local%20bar%20warm%20amber%20light%20authentic%20shitamachi%20atmosphere%20close%20up%20food&width=600&height=400&seq=timeline-step4&orientation=landscape',
      },
      {
        time: '20:30',
        icon: 'ri-beer-line',
        label: '3軒目',
        title: 'シメの一杯',
        description: '3軒目はあなたのリクエストに合わせて。日本酒バー・クラフトビール・ハイボール専門店など、気分で選びます。',
        tag: 'お好みで選択',
        color: 'amber',
        image: 'https://readdy.ai/api/search-image?query=Japanese%20sake%20bar%20craft%20beer%20highball%20whisky%20cozy%20intimate%20small%20bar%20Tokyo%20night%20wooden%20counter%20bottles%20lined%20up%20warm%20dim%20lighting%20bartender%20glass%20drinks%20local%20neighborhood%20bar&width=600&height=400&seq=timeline-step5&orientation=landscape',
      },
      {
        time: '21:30',
        icon: 'ri-star-smile-line',
        label: 'フィニッシュ',
        title: '解散・お見送り',
        description: '最寄り駅までお見送り。気に入ったお店があれば次回一人でも来られるよう、地図や情報をシェアします。',
        tag: '思い出をお土産に',
        color: 'green',
        image: 'https://readdy.ai/api/search-image?query=Tokyo%20local%20train%20station%20night%20platform%20farewell%20friends%20waving%20goodbye%20warm%20station%20lights%20happy%20tourists%20smiling%20memories%20Japan%20evening%20suburban%20station%20cozy%20atmosphere&width=600&height=400&seq=timeline-step6&orientation=landscape',
      },
    ],
    note: '※ 上記はあくまで目安の流れです。当日の雰囲気やご要望に合わせて柔軟に対応します。',
    cta: 'WhatsAppで予約する',
  },
  en: {
    badge: 'How It Works',
    title: 'Your Night Out, Step by Step',
    subtitle: 'Senbero bars, bar-hopping, local izakayas — experience the real Tokyo nightlife together',
    steps: [
      {
        time: '6:00 PM',
        icon: 'ri-map-pin-2-line',
        label: 'Start',
        title: 'Meet Up',
        description: 'We meet at a local station in Ebara-machi, Nakanobu, Togoshi, or Musashi-Koyama — areas tourists rarely visit.',
        tag: 'Local Station',
        color: 'amber',
        image: 'https://readdy.ai/api/search-image?query=Tokyo%20local%20train%20station%20evening%20dusk%20Togoshi%20Musashi-Koyama%20retro%20shotengai%20shopping%20street%20warm%20orange%20lantern%20light%20nostalgic%20Showa%20era%20atmosphere%20people%20walking%20narrow%20street%20Japan&width=600&height=400&seq=timeline-step1&orientation=landscape',
      },
      {
        time: '6:15 PM',
        icon: 'ri-store-2-line',
        label: '1st Stop',
        title: 'Senbero Bar',
        description: 'Kick off at a "senbero" spot — where ¥1,000 gets you drinks and snacks. Sit alongside local regulars for an authentic experience.',
        tag: 'From ¥1,000',
        color: 'orange',
        image: 'https://readdy.ai/api/search-image?query=Japanese%20senbero%20izakaya%20bar%20interior%20counter%20seats%20local%20old%20men%20drinking%20beer%20sake%20warm%20yellow%20light%20small%20cramped%20authentic%20Tokyo%20shitamachi%20cheap%20standing%20bar%20smoky%20atmosphere%20retro%20signboard&width=600&height=400&seq=timeline-step2&orientation=landscape',
      },
      {
        time: '7:15 PM',
        icon: 'ri-walk-line',
        label: 'Walk',
        title: 'Stroll the Shotengai',
        description: 'Walk through a retro Showa-era shopping street on the way to the next spot. Hidden alleyways and local secrets included.',
        tag: '5 min walk',
        color: 'yellow',
        image: 'https://readdy.ai/api/search-image?query=Tokyo%20Showa%20retro%20shotengai%20shopping%20arcade%20night%20illuminated%20lanterns%20colorful%20banners%20narrow%20covered%20street%20local%20shops%20warm%20glow%20nostalgic%20Japan%20evening%20people%20strolling%20old%20fashioned%20signboards&width=600&height=400&seq=timeline-step3&orientation=landscape',
      },
      {
        time: '7:30 PM',
        icon: 'ri-goblet-line',
        label: '2nd Stop',
        title: 'Bar Hopping',
        description: 'Yakitori, motsu-yaki, nikomi stew — classic Tokyo bar snacks with cold drinks. Chat with local regulars if you\'re up for it.',
        tag: 'Yakitori & Motsu',
        color: 'red',
        image: 'https://readdy.ai/api/search-image?query=Japanese%20yakitori%20motsu%20yaki%20grilled%20skewers%20izakaya%20counter%20charcoal%20grill%20smoke%20rising%20beer%20glasses%20nikomi%20stew%20Tokyo%20local%20bar%20warm%20amber%20light%20authentic%20shitamachi%20atmosphere%20close%20up%20food&width=600&height=400&seq=timeline-step4&orientation=landscape',
      },
      {
        time: '8:30 PM',
        icon: 'ri-beer-line',
        label: '3rd Stop',
        title: 'Nightcap',
        description: 'The final stop is your call — sake bar, craft beer, highball specialist. We pick based on the vibe of the night.',
        tag: 'Your Choice',
        color: 'amber',
        image: 'https://readdy.ai/api/search-image?query=Japanese%20sake%20bar%20craft%20beer%20highball%20whisky%20cozy%20intimate%20small%20bar%20Tokyo%20night%20wooden%20counter%20bottles%20lined%20up%20warm%20dim%20lighting%20bartender%20glass%20drinks%20local%20neighborhood%20bar&width=600&height=400&seq=timeline-step5&orientation=landscape',
      },
      {
        time: '9:30 PM',
        icon: 'ri-star-smile-line',
        label: 'Finish',
        title: 'See You Off',
        description: 'We walk you to the nearest station. If you loved a spot, we\'ll share the map and info so you can return on your own.',
        tag: 'Take the memories home',
        color: 'green',
        image: 'https://readdy.ai/api/search-image?query=Tokyo%20local%20train%20station%20night%20platform%20farewell%20friends%20waving%20goodbye%20warm%20station%20lights%20happy%20tourists%20smiling%20memories%20Japan%20evening%20suburban%20station%20cozy%20atmosphere&width=600&height=400&seq=timeline-step6&orientation=landscape',
      },
    ],
    note: '* The above is a sample flow. We adapt flexibly based on your preferences and the mood of the night.',
    cta: 'Book via WhatsApp',
  },
};

const colorMap: Record<string, { bg: string; text: string; border: string; dot: string; tag: string }> = {
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-300',
    dot: 'bg-amber-500',
    tag: 'bg-amber-100 text-amber-700',
  },
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-300',
    dot: 'bg-orange-500',
    tag: 'bg-orange-100 text-orange-700',
  },
  yellow: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-300',
    dot: 'bg-yellow-500',
    tag: 'bg-yellow-100 text-yellow-700',
  },
  red: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-300',
    dot: 'bg-red-500',
    tag: 'bg-red-100 text-red-700',
  },
  green: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-300',
    dot: 'bg-green-500',
    tag: 'bg-green-100 text-green-700',
  },
};

export const IzakayaTimeline: React.FC<IzakayaTimelineProps> = ({ lang }) => {
  const t = content[lang];
  const [activeStep, setActiveStep] = useState<number | null>(null);

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4 tracking-wide">
            {t.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {t.title}
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[52px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-300 via-orange-300 to-green-300 hidden md:block" />

          <div className="space-y-6">
            {t.steps.map((step, index) => {
              const colors = colorMap[step.color] || colorMap.amber;
              const isActive = activeStep === index;

              return (
                <div
                  key={index}
                  className="relative flex gap-6 cursor-pointer group"
                  onClick={() => setActiveStep(isActive ? null : index)}
                >
                  {/* Left: time + dot */}
                  <div className="hidden md:flex flex-col items-center w-[52px] shrink-0 pt-1">
                    <span className="text-xs font-bold text-gray-400 whitespace-nowrap mb-2 leading-none">
                      {step.time}
                    </span>
                    <div className={`w-5 h-5 rounded-full border-4 border-white shadow-md z-10 transition-transform duration-300 group-hover:scale-125 ${colors.dot}`} />
                  </div>

                  {/* Card */}
                  <div
                    className={`flex-1 rounded-2xl border overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md ${
                      isActive ? `${colors.border} shadow-md` : 'border-gray-100'
                    }`}
                  >
                    {/* Card Header */}
                    <div className={`p-5 transition-colors duration-300 ${isActive ? colors.bg : 'bg-white'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4">
                          {/* Icon */}
                          <div className={`w-12 h-12 flex items-center justify-center rounded-xl shrink-0 transition-colors duration-300 ${isActive ? 'bg-white/60' : 'bg-gray-50'}`}>
                            <i className={`${step.icon} text-2xl ${isActive ? colors.text : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.tag}`}>
                                {step.label}
                              </span>
                              {/* Mobile time */}
                              <span className="text-xs text-gray-400 md:hidden">{step.time}</span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 leading-snug">
                              {step.title}
                            </h3>
                          </div>
                        </div>
                        {/* Tag + chevron */}
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`hidden sm:inline-block text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${colors.tag}`}>
                            {step.tag}
                          </span>
                          <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${isActive ? 'rotate-180 ' + colors.text : 'text-gray-300'}`} />
                        </div>
                      </div>
                    </div>

                    {/* Expandable: image + description */}
                    <div
                      className={`overflow-hidden transition-all duration-500 ${
                        isActive ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      {/* Image */}
                      <div className="w-full h-52 overflow-hidden">
                        <img
                          src={step.image}
                          alt={step.title}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      {/* Description */}
                      <div className={`px-6 py-4 ${colors.bg}`}>
                        <p className="text-gray-700 leading-relaxed text-sm">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {/* Always-visible description on desktop when not active */}
                    <div
                      className={`px-6 pb-4 transition-all duration-300 ${
                        isActive ? 'hidden' : 'hidden md:block bg-white'
                      }`}
                    >
                      <p className="text-gray-500 leading-relaxed text-sm pl-16">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Note */}
        <p className="text-center text-gray-400 text-sm mt-10 mb-8">{t.note}</p>

        {/* CTA */}
        <div className="text-center">
          <a
            href={`https://wa.me/819092895522?text=${encodeURIComponent("Hi! I'd like to book the Tokyo Local Izakaya Night tour — senbero bars, bar-hopping, and local izakayas in Ebara-cho / Nakanobu / Togoshi / Musashi-Koyama. Please let me know your available dates!")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-2xl" />
            {t.cta}
          </a>
        </div>
      </div>
    </section>
  );
};
